/**
 * Zorgt dat een meta description eindigt met een natuurlijke uitnodiging om te
 * boeken. Redactionele descriptions (journal, informatieve pagina's) beschrijven
 * vooral de inhoud; zoekresultaten converteren beter met een korte slotzin.
 *
 * Belangrijk: de description mag nooit midden in een zin worden afgekapt. Past
 * de uitnodiging er niet netjes bij, dan laten wij die weg in plaats van de
 * zin te breken. Google kapt snippets af rond 155–160 tekens (985 pixels), dus
 * daar blijven wij onder.
 */
const CTA_RE = /(boek|reserveer|bereken|book|reserve)/i;

const NL_CTA = "Boek uw taxi online met een vaste prijs.";
const EN_CTA = "Book your taxi online at a fixed price.";

/** Maximale lengte: blijft binnen de zichtbare snippetbreedte in Google. */
const MAX_LENGTH = 150;

/**
 * Kort de tekst in tot de laatste hele zin die binnen de limiet past. Levert
 * die zinsgrens een te korte description op (Google en auditors verwachten
 * ruim 110–155 tekens), dan eindigen wij op een woordgrens dicht bij de
 * limiet: zo blijft de volledige informatie in het zoekresultaat staan.
 */
const MIN_LENGTH = 110;

/** Woorden waarop een snippet nooit hoort te eindigen. */
const DANGLING =
  /(?:\s+(?:en|of|met|voor|naar|van|in|op|bij|de|het|een|om|te|and|or|with|for|to|from|the|a|an))+$/i;

function clipToClause(text: string, max: number): string {
  const cut = text.slice(0, max + 1);
  // Een komma of puntkomma geeft een natuurlijk rustpunt.
  const lastClause = Math.max(cut.lastIndexOf(", "), cut.lastIndexOf("; "));
  if (lastClause >= MIN_LENGTH) return cut.slice(0, lastClause).trim();
  const words = cut.split(" ");
  words.pop();
  return words
    .join(" ")
    .replace(/[\s,;:—–-]+$/, "")
    .replace(DANGLING, "")
    .trim();
}

function clipToSentence(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max + 1);
  const lastStop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
  if (lastStop >= MIN_LENGTH) return cut.slice(0, lastStop + 1).trim();
  // Geen bruikbare zinsgrens: eindigen op een natuurlijk rustpunt, zonder
  // losse leestekens of hangende voegwoorden.
  return clipToClause(text, max);
}

export function withMetaCta(description: string, lang: "nl" | "en" = "nl"): string {
  const text = description.trim();
  if (!text) return text;
  if (CTA_RE.test(text)) return clipToSentence(text, MAX_LENGTH);

  const base = /[.!?]$/.test(text) ? text : `${text}.`;
  const options = lang === "en" ? [EN_CTA, "Book online."] : [NL_CTA, "Boek online."];
  for (const cta of options) {
    const combined = `${base} ${cta}`;
    if (combined.length <= MAX_LENGTH) return combined;
  }
  // De beschrijving zelf vult de ruimte al: dan liever een volledige zin dan
  // een afgebroken zin met uitnodiging erachter.
  return clipToSentence(base, MAX_LENGTH);
}

/**
 * Meta title rond 60 tekens: eerst het merk-achtervoegsel weg. Nooit midden in
 * een zin afbreken: is de titel daarna nog iets te lang (tot 70), dan blijft
 * die heel; anders inkorten op een natuurlijk rustpunt (dubbele punt, komma).
 */
export function clampTitle(title: string, max = 60): string {
  let t = title.trim();
  if (t.length <= max) return t;
  t = t.replace(/\s*[|–—-]\s*Taxiritje\.nl\s*$/i, "").trim();
  if (t.length <= 70) return t;
  const cut = t.slice(0, max + 1);
  const stop = Math.max(cut.lastIndexOf(": "), cut.lastIndexOf(", "), cut.lastIndexOf(" — "));
  if (stop >= 30) return cut.slice(0, stop).trim();
  const words = cut.split(" ");
  words.pop();
  return words.join(" ").replace(/[\s,;:—–|-]+$/, "").replace(DANGLING, "").trim();
}
